import { runResult } from "../runResult.js";
import { fetchTopScores, submitScore, supabaseConfigured } from "../supabaseClient.js";

// DOM-only overlay, not a SceneManager screen - the earth/orbit camera
// behind it is gameScreen's own (see gameScreen.js's "end" mode), the same
// backdrop the menu uses, so there's no separate scene to load here.
const el = document.getElementById("screen-leaderboard");
const cardEl = document.querySelector("#screen-leaderboard .leaderboard-card");
const headingEl = document.getElementById("leaderboard-heading");
const timeEl = document.getElementById("leaderboard-time");
const form = document.getElementById("leaderboard-form");
const handleInput = document.getElementById("lb-handle");
const statusEl = document.getElementById("leaderboard-status");
// This button now means "back to menu" - it's available whether or not
// the player has submitted their score.
const skipBtn = document.getElementById("btn-leaderboard-skip");

// "View Top 100" opens a modal reusing the same .modal-overlay/.modal-box
// chrome as the rules modal - a separate small DOM tree, not tied to this
// screen's show()/hide() lifecycle any more than the rules modal is, since
// it can be opened while the leaderboard entry screen is showing.
const top100Modal = document.getElementById("top100-modal");
const top100List = document.getElementById("top100-list");
const top100CloseBtn = document.getElementById("btn-top100-close");

const RSVP_URL = "https://laylo.com/namesbliss/QPKOYS";

let listContainer = null;
function getListContainer() {
  if (!listContainer) {
    listContainer = document.createElement("div");
    listContainer.id = "leaderboard-list";
    cardEl.appendChild(listContainer);

    // Lives inside the same card as the top-5 list it expands from, right
    // below it - not a separate always-there control, since it only makes
    // sense once there's a board to expand.
    const expandBtn = document.createElement("button");
    expandBtn.type = "button";
    expandBtn.id = "btn-leaderboard-expand";
    expandBtn.className = "leaderboard-expand-btn";
    expandBtn.textContent = "Top 100";
    expandBtn.addEventListener("click", openTop100);
    cardEl.appendChild(expandBtn);
  }
  return listContainer;
}

/** Builds one .leaderboard-row for entry at zero-based `index`. Shared by
 * the top-5 list and the top-100 modal so their markup can't drift apart.
 * `clickable` wires the row itself to open the Top 100 view - only used
 * for the top-5 list, since a row inside that modal already is the
 * expanded view. */
function renderRow(entry, index, { clickable = false } = {}) {
  const row = document.createElement("div");
  row.className = "leaderboard-row";
  if (index < 3) row.classList.add(`rank-${index + 1}`);
  if (clickable) {
    row.classList.add("leaderboard-row-clickable");
    row.setAttribute("role", "button");
    row.setAttribute("tabindex", "0");
    row.setAttribute("aria-label", "View top 100");
    row.addEventListener("click", openTop100);
    row.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTop100(); }
    });
  }

  const rank = document.createElement("span");
  rank.className = "leaderboard-rank";
  rank.textContent = String(index + 1);

  const handle = document.createElement("span");
  handle.className = "leaderboard-name";
  const handleText = document.createElement("span");
  handleText.textContent = entry.handle ? `@${entry.handle}` : "anon"; // textContent, not innerHTML - handles are user-submitted
  handle.appendChild(handleText);

  const score = document.createElement("span");
  score.className = "leaderboard-score";
  score.textContent = `${Number(entry.seconds).toFixed(2)}s`;

  row.append(rank, handle, score);
  return row;
}

async function loadTopScores() {
  const container = getListContainer();
  container.textContent = "";

  if (!supabaseConfigured) {
    const notice = document.createElement("div");
    notice.className = "leaderboard-notice";
    notice.textContent = "Leaderboard not connected yet - add your Supabase project to .env to go live.";
    container.appendChild(notice);
    return;
  }

  const title = document.createElement("div");
  title.className = "leaderboard-title";
  title.textContent = "Top times";
  container.appendChild(title);

  const entries = await fetchTopScores(5);
  if (entries.length === 0) {
    const empty = document.createElement("div");
    empty.className = "leaderboard-notice";
    empty.textContent = "No one found Bliss yet";
    container.appendChild(empty);
    return;
  }

  entries.forEach((entry, index) => container.appendChild(renderRow(entry, index, { clickable: true })));
}

async function openTop100() {
  top100Modal.classList.add("visible");
  top100List.textContent = "";

  if (!supabaseConfigured) {
    const notice = document.createElement("div");
    notice.className = "leaderboard-notice";
    notice.textContent = "Leaderboard not connected yet - add your Supabase project to .env to go live.";
    top100List.appendChild(notice);
    return;
  }

  const loading = document.createElement("div");
  loading.className = "leaderboard-notice";
  loading.textContent = "Loading...";
  top100List.appendChild(loading);

  const entries = await fetchTopScores(100);
  top100List.textContent = "";
  if (entries.length === 0) {
    const empty = document.createElement("div");
    empty.className = "leaderboard-notice";
    empty.textContent = "No one found Bliss yet";
    top100List.appendChild(empty);
    return;
  }

  entries.forEach((entry, index) => top100List.appendChild(renderRow(entry, index)));
}

top100CloseBtn?.addEventListener("click", () => top100Modal.classList.remove("visible"));
top100Modal?.addEventListener("click", (e) => {
  if (e.target === top100Modal) top100Modal.classList.remove("visible");
});

let onSubmit = null;
let onSkip = null;
// Remembered across rounds purely so the handle field doesn't clear itself
// after a submit - nothing outside this module needs it.
let savedHandle = "";

export const leaderboardScreen = {
  show({ onDone } = {}) {
    el.classList.remove("hidden");

    const found = runResult.found;
    const seconds = runResult.elapsedSeconds;
    headingEl.textContent = found ? "You found Bliss!" : "Time's up!";
    timeEl.textContent = found ? `Your time: ${seconds.toFixed(2)}s` : "Bliss got away this time.";
    handleInput.value = savedHandle;
    statusEl.textContent = "";

    // Only a completed catch is a real time - a timeout has nothing worth
    // ranking, so there's no submit form to show at all.
    form.style.display = found ? "" : "none";

    // Fresh round - reset Submit Score back from whatever the last round
    // may have left it as (see the morph into Lively Hat Drop below).
    const submitBtn = form.querySelector("button");
    submitBtn.type = "submit";
    submitBtn.textContent = "SUBMIT SCORE";
    submitBtn.classList.remove("btn-glow");
    submitBtn.onclick = null;
    submitBtn.disabled = false;

    loadTopScores();

    onSubmit = async (e) => {
      e.preventDefault();
      if (!runResult.found) return; // belt-and-suspenders - form is hidden in this case, but never trust just that
      // Lowercased client-side too (not just by submit_leaderboard_score()
      // server-side) so the input remembers/displays the same casing that
      // actually got stored, instead of flashing mixed case for a moment.
      savedHandle = handleInput.value.trim().replace(/^@+/, "").toLowerCase(); // strip a leading @ in case they typed one - the field already prefixes it
      submitBtn.disabled = true;
      try {
        const { bestSeconds, isNewBest } = await submitScore({
          handle: savedHandle,
          found: runResult.found,
          sessionId: runResult.sessionId,
        });
        // Silent on a new best - the updated leaderboard list below is
        // confirmation enough. Only speak up when this run *didn't* win,
        // since that's the surprising case worth explaining.
        statusEl.textContent = isNewBest ? "" : `Kept your best of ${Number(bestSeconds).toFixed(2)}s`;
        await loadTopScores();
        // The score is in - this button's job here is done, so it morphs
        // into the round's next call to action (glowing to draw the eye)
        // instead of just sitting there disabled.
        submitBtn.disabled = false;
        submitBtn.type = "button";
        submitBtn.textContent = "LIVELY HAT DROP";
        submitBtn.classList.add("btn-glow");
        submitBtn.onclick = () => window.open(RSVP_URL, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.warn("[leaderboard] submit failed - check that supabase/schema.sql is applied and .env has your project's URL/publishable key.", err);
        statusEl.textContent = supabaseConfigured ? "Couldn't save, try again." : "Leaderboard not connected yet.";
        submitBtn.disabled = false; // let them retry
      }
      // Deliberately NOT calling onDone() here - the player stays on this
      // screen to see the (now updated) leaderboard, and leaves via the
      // explicit back-to-menu button below.
    };
    onSkip = () => {
      leaderboardScreen.hide();
      onDone?.();
    };
    form.addEventListener("submit", onSubmit);
    skipBtn.addEventListener("click", onSkip);
  },

  hide() {
    el.classList.add("hidden");
    form.removeEventListener("submit", onSubmit);
    skipBtn.removeEventListener("click", onSkip);
  },
};
